
import { useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';

export function useSpeechRecognition(isAuthenticated: boolean) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const onTranscriptReadyRef = useRef<(text: string) => void>(() => {});

  // Convert speech to text using ElevenLabs Scribe v1 model
  const convertSpeechToText = async (audioBlob: Blob): Promise<string> => {
    try {
      // Check authentication
      if (!isAuthenticated) {
        toast({
          title: 'Authentication Required',
          description: 'Please sign in to use voice features.',
          variant: 'destructive'
        });
        return '';
      }

      console.log('Converting speech to text with ElevenLabs Scribe v1 model');
      
      // Convert blob to base64
      const buffer = await audioBlob.arrayBuffer();
      const base64Audio = btoa(
        String.fromCharCode(...new Uint8Array(buffer))
      );

      // Call the Supabase Edge Function for speech-to-text
      const { data, error } = await supabase.functions.invoke('elevenlabs', {
        body: {
          action: 'stt',
          audioData: base64Audio
        }
      });

      if (error) {
        console.error('Speech recognition error:', error);
        toast({
          title: 'Speech Recognition Error',
          description: 'Failed to convert your speech to text. Please try again.',
          variant: 'destructive'
        });
        return '';
      }

      if (!data.success) {
        console.error('ElevenLabs error:', data.error);
        toast({
          title: 'Speech Recognition Error',
          description: data.error || 'Failed to convert speech to text.',
          variant: 'destructive'
        });
        return '';
      }

      console.log('Received transcript:', data.text);
      const recognizedText = data.text || '';
      setTranscript(recognizedText);
      return recognizedText;
    } catch (error) {
      console.error('Error in speech to text conversion:', error);
      toast({
        title: 'Speech Recognition Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive'
      });
      return '';
    }
  };

  // Start listening for speech
  const startListening = async () => {
    // Check authentication
    if (!isAuthenticated) {
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to use voice features.',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      // Check browser support
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast({
          title: 'Browser Compatibility',
          description: 'Your browser does not support microphone access.',
          variant: 'destructive'
        });
        return;
      }

      console.log('Starting speech recognition...');
      
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Create media recorder
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Setup event handlers
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        console.log('Recording stopped, processing audio...');
        
        // Combine audio chunks into a single blob
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        // Convert speech to text
        const text = await convertSpeechToText(audioBlob);
        
        if (text) {
          console.log('Speech recognized:', text);
          // Call the callback function with the recognized text
          onTranscriptReadyRef.current(text);
        }
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        setIsListening(false);
      };

      // Start recording
      mediaRecorder.start();
      setIsListening(true);
      
      // Auto-stop recording after 10 seconds
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          console.log('Auto-stopping recording after timeout');
          stopListening();
        }
      }, 10000);
      
      toast({
        title: 'Listening',
        description: 'Speak now. Recording will stop automatically after 10 seconds.',
      });
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast({
        title: 'Microphone Access Error',
        description: 'Failed to access your microphone. Please check permissions.',
        variant: 'destructive'
      });
      setIsListening(false);
    }
  };

  // Stop listening
  const stopListening = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      console.log('Manually stopping recording');
      mediaRecorderRef.current.stop();
    }
  };

  // Set callback for when transcript is ready
  const setOnTranscriptReady = (callback: (text: string) => void) => {
    console.log('Setting transcript ready callback');
    onTranscriptReadyRef.current = callback;
  };

  return {
    isListening,
    transcript,
    startListening,
    stopListening,
    setOnTranscriptReady
  };
}
